'use client';

import { use } from 'react';
import { BuyerListingFavorite } from '@/legacy/favorites/FavoritesViewModel';
import { useGhostStore } from '@/legacy/store';
import { getTitle, removeParentheticals } from '@/lib/getList';
import { PercentOffMsrpClassification } from '@/types/PercentOffMsrpClassification';
import { FilterAttribute } from '@/types/filterAttribute';
import { Background, FlatBuyerPreferences, filterAttributeToBuyerPreferenceKeyMap } from '../types';
import GenericCollection from './GenericCollection';

type Props = {
  background?: Background;
  sectionTitle: string;
  navigateTo: string;
  numPerPage: number;
  favoritesPromise: Promise<BuyerListingFavorite[]>;
  listingsIndexName: string;
  seeMoreStringComponent?: JSX.Element;
  filterAttribute: FilterAttribute;
  preferencesPromise: Promise<FlatBuyerPreferences>;
  index: number;
  msrpClassificationsPromise: Promise<PercentOffMsrpClassification[]>;
  isStockXSectionEnabled: boolean;
};

const RecommendationCollectionContentForMsrpAttribute = ({
  preferencesPromise,
  msrpClassificationsPromise,
  ...rest
}: Omit<Props, 'filterAttribute' | 'description' | 'sectionTitle'>) => {
  const preferences = use(preferencesPromise);
  const msrpClassifications = use(msrpClassificationsPromise);
  const buyerPrefKeyForMsrp = filterAttributeToBuyerPreferenceKeyMap['percentOffMsrp'];
  const preferenceValue = preferences[buyerPrefKeyForMsrp];
  const buyerId = useGhostStore((state) => state.user)?.buyer?.id;
  if (preferenceValue === null) return null;
  const classification = msrpClassifications.find(({ id }) => id === preferenceValue);
  if (!classification) return null;
  const { percentOffClassification, percentOffMinValue, percentOffMaxValue } = classification;
  const title = percentOffClassification === 'offprice' ? 'Off-Price' : 'Wholesale';
  const stealthListingFilter = buyerId
    ? `(stealthApprovedBuyerIds:${buyerId} OR isStealth:false)`
    : 'isStealth:false';
  const filters = `${stealthListingFilter} AND percentOffMsrp >= ${percentOffMinValue} AND percentOffMsrp <= ${percentOffMaxValue}`;

  return <GenericCollection configureProps={{ filters }} {...rest} sectionTitle={title} />;
};

function RecommendationCollectionContent({ filterAttribute, sectionTitle, ...rest }: Props) {
  if (filterAttribute === 'percentOffMsrp') {
    return <RecommendationCollectionContentForMsrpAttribute {...rest} />;
  }
  return (
    <RecommendationCollectionContentForStringArrayAttribute
      sectionTitle={sectionTitle}
      filterAttribute={filterAttribute}
      {...rest}
    />
  );
}

const RecommendationCollectionContentForStringArrayAttribute = ({
  filterAttribute,
  preferencesPromise,
  sectionTitle,
  ...rest
}: Omit<Props, 'filterAttribute' | 'msrpClassificationsPromise'> & {
  filterAttribute: Exclude<FilterAttribute, 'percentOffMsrp'>;
}) => {
  const preferences = use(preferencesPromise);
  const buyerPrefKey = filterAttributeToBuyerPreferenceKeyMap[filterAttribute];
  const preferenceValues = preferences[buyerPrefKey];
  const buyerId = useGhostStore((state) => state.user)?.buyer?.id;
  if (preferenceValues.length === 0) return null;

  const stealthListingFilter = buyerId
    ? `(isStealth:false OR stealthApprovedBuyerIds:${buyerId})`
    : 'isStealth:false';

  const preferenceValuesFilter = preferenceValues
    .map((value) => `${filterAttribute}:"${value}"`)
    .join(' OR ');

  const filters = `${stealthListingFilter} AND (${preferenceValuesFilter})`;

  const title = getTitle(
    filterAttribute === 'compositeCategories'
      ? preferenceValues.map((pv) => pv.split('|')[1]).map(removeParentheticals)
      : preferenceValues.map(removeParentheticals),
    sectionTitle,
  );

  return <GenericCollection configureProps={{ filters }} {...rest} sectionTitle={title} />;
};

export default function RecommendationCollection(props: Props): JSX.Element {
  return <RecommendationCollectionContent {...props} />;
}
